import type { DifficultyMode, FuelFeature, FuelGeoJson, PriceMode, RouteFeature, TollGeoJson, TrafficStyle } from '../types';

type FontStack = string[];
type FeatureCollection = { type: 'FeatureCollection'; features: any[]; [key: string]: unknown };

interface JourneyLayerRuntime {
  isStyleLoaded(): boolean;
  getStyle(): { layers?: Array<{ id: string; type?: string }> } | undefined;
  getLayer(id: string): any;
  getSource(id: string): any;
  addSource(id: string, source: any): unknown;
  removeSource(id: string): unknown;
  addLayer(layer: any, beforeId?: string): unknown;
  removeLayer(id: string): unknown;
  setLayoutProperty(id: string, property: string, value: unknown): unknown;
  setPaintProperty(id: string, property: string, value: unknown): unknown;
  setFilter(id: string, filter: unknown): unknown;
  queryRenderedFeatures(options?: unknown): any[];
  on(event: string, layerId: string, handler: (...args: any[]) => void): unknown;
  off?(event: string, layerId: string, handler: (...args: any[]) => void): unknown;
  getCanvas(): { style: { cursor: string } };
}

type RegisterLayerGroup = (id: string, apply: () => void | Promise<void>) => () => void;

export interface FuelLayerState {
  data: FuelGeoJson | null;
  active: boolean;
  mode: 'relative' | 'absolute';
  range: { min: number; max: number };
  fuelTypeLabel: string;
  priceMode: PriceMode;
  corridor: { active: boolean; routes: RouteFeature[]; bufferKm: number };
  textFont: FontStack;
}

export interface TollLayerState {
  data: TollGeoJson | null;
  active: boolean;
  activeRouteNums: number[];
  textFont: FontStack;
}

export interface TrafficLayerState { active: boolean; style: TrafficStyle }
export interface BaustellenLayerState { active: boolean; data: FeatureCollection | null; textFont: FontStack }
export interface DifficultyLayerState {
  active: boolean;
  mode: DifficultyMode;
  activeRouteNums: number[];
  segments: FeatureCollection | null;
  hairpins: FeatureCollection | null;
  textFont: FontStack;
}

export interface FuelLayerStats { min: number; max: number; mixedUnits: boolean; scopedCount: number }
export interface FuelHistogramSnapshot { prices: number[]; mixedUnits: boolean }
export interface JourneyPopup { coordinate: [number, number]; html: string; offset: number }

export interface JourneyLayerEvents {
  showPopup?(popup: JourneyPopup): void;
  onFuelStats?(stats: FuelLayerStats | null): void;
  onFuelHistogram?(histogram: FuelHistogramSnapshot | null): void;
}

export interface JourneyLayerController {
  setEvents(events: JourneyLayerEvents): void;
  applyFuel(state: FuelLayerState): void;
  refreshFuelViewport(): void;
  applyTolls(state: TollLayerState): void;
  applyTraffic(state: TrafficLayerState): void;
  applyBaustellen(state: BaustellenLayerState): void;
  applyDifficulty(state: DifficultyLayerState): void;
  dispose(): void;
}

function firstSymbolLayerId(runtime: JourneyLayerRuntime) {
  return runtime.getStyle()?.layers?.find((layer) => layer.type === 'symbol')?.id;
}

function removeLayer(runtime: JourneyLayerRuntime, id: string) {
  if (runtime.getLayer(id)) runtime.removeLayer(id);
}

function removeSource(runtime: JourneyLayerRuntime, id: string) {
  if (runtime.getSource(id)) runtime.removeSource(id);
}

function activeRouteFilter(routeNums: number[]) {
  return ['in', ['get', 'route_num'], ['literal', routeNums]];
}

function escapeHtml(value: unknown) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function extractFirstHttpUrl(value: unknown) {
  const match = String(value ?? '').match(/https?:\/\/[^\s"'<]+/i);
  if (!match) return '';
  try {
    const url = new URL(match[0]);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : '';
  } catch {
    return '';
  }
}

function fuelColorExpr(lo: number, hi: number) {
  const safeHi = hi > lo ? hi : lo + 0.01;
  return ['interpolate', ['linear'], ['get', 'p'], lo, '#22c55e', (lo + safeHi) / 2, '#eab308', safeHi, '#ef4444'];
}

function formatPrice(feature: FuelFeature, priceMode: PriceMode) {
  const price = Number(feature.properties.p || 0);
  if (!price) return '—';
  if (priceMode === 'eur_l') return `${price.toFixed(3)} EUR/L`;
  return `${price.toFixed(3)} ${(feature.properties.u || 'EUR/L').replace('_', '/')}`;
}

function brandLabel(feature: FuelFeature) {
  return String(feature.properties.rb || feature.properties.bk || feature.properties.n || 'Fuel').replace(/[_-]+/g, ' ').trim();
}

function approxDistKm(lon1: number, lat1: number, lon2: number, lat2: number) {
  const dLat = (lat2 - lat1) * 111.32;
  const dLon = (lon2 - lon1) * 111.32 * Math.cos(((lat1 + lat2) / 2) * Math.PI / 180);
  return Math.sqrt(dLat * dLat + dLon * dLon);
}

export function filterFuelNearRoutes(data: FuelGeoJson, routes: RouteFeature[], bufferKm: number): FuelGeoJson {
  const routeCoords = routes.flatMap((route) => route.geometry.coordinates);
  if (!routeCoords.length) return data;
  const bufferDeg = (bufferKm / 111.32) * 1.5;
  let minLon = Infinity, maxLon = -Infinity, minLat = Infinity, maxLat = -Infinity;
  routeCoords.forEach(([lon, lat]) => {
    minLon = Math.min(minLon, lon); maxLon = Math.max(maxLon, lon);
    minLat = Math.min(minLat, lat); maxLat = Math.max(maxLat, lat);
  });
  const samplePoints = routeCoords.filter((_, index) => index % 3 === 0);
  if (samplePoints[samplePoints.length - 1] !== routeCoords[routeCoords.length - 1]) samplePoints.push(routeCoords[routeCoords.length - 1]);
  const features = data.features.filter((feature) => {
    const [lon, lat] = feature.geometry.coordinates;
    if (lon < minLon - bufferDeg || lon > maxLon + bufferDeg || lat < minLat - bufferDeg || lat > maxLat + bufferDeg) return false;
    return samplePoints.some((point) => approxDistKm(lon, lat, point[0], point[1]) <= bufferKm);
  });
  return { type: 'FeatureCollection', metadata: data.metadata, features };
}

export function createJourneyLayerController(
  runtime: JourneyLayerRuntime,
  registerLayerGroup: RegisterLayerGroup,
  initialEvents: JourneyLayerEvents = {}
): JourneyLayerController {
  let events = initialEvents;
  let fuelState: FuelLayerState | null = null;
  let tollState: TollLayerState | null = null;
  let trafficState: TrafficLayerState | null = null;
  let baustellenState: BaustellenLayerState | null = null;
  let difficultyState: DifficultyLayerState | null = null;
  let mixedFuelUnits = false;
  let disposed = false;
  const handlers = new Map<string, { event: string; layer: string; handler: (...args: any[]) => void }>();

  const bind = (id: string, event: string, layer: string, handler: (...args: any[]) => void) => {
    const previous = handlers.get(id);
    if (previous) runtime.off?.(previous.event, previous.layer, previous.handler);
    handlers.set(id, { event, layer, handler });
    runtime.on(event, layer, handler);
  };

  const applyFuelRuntime = () => {
    if (disposed || !runtime.isStyleLoaded()) return;
    const state = fuelState;
    if (!state?.data) {
      events.onFuelStats?.(null);
      events.onFuelHistogram?.(null);
      ['fuel-circles', 'fuel-labels'].forEach((id) => {
        if (runtime.getLayer(id)) runtime.setLayoutProperty(id, 'visibility', 'none');
      });
      return;
    }
    const data = state.corridor.active
      ? filterFuelNearRoutes(state.data, state.corridor.routes, state.corridor.bufferKm)
      : state.data;
    const prices = data.features.map((feature) => Number(feature.properties.p || 0)).filter((value) => value > 0 && value < 20);
    const globalMin = prices.length ? Math.min(...prices) : 0;
    const globalMax = prices.length ? Math.max(...prices) : 3;
    mixedFuelUnits = (state.data.metadata?.units || ['EUR/L']).length > 1;
    events.onFuelStats?.({ min: globalMin, max: globalMax, mixedUnits: mixedFuelUnits, scopedCount: data.features.length });
    const source = runtime.getSource('fuel-prices');
    if (source) source.setData(data);
    else runtime.addSource('fuel-prices', { type: 'geojson', data });
    if (!runtime.getLayer('fuel-circles')) runtime.addLayer({
      id: 'fuel-circles', type: 'circle', source: 'fuel-prices',
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 2, 1.5, 6, 2.5, 10, 5, 14, 7],
        'circle-color': fuelColorExpr(globalMin, globalMax), 'circle-stroke-color': '#ffffff',
        'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], 2, 0, 8, 1],
        'circle-opacity': ['interpolate', ['linear'], ['zoom'], 2, state.active ? 0.7 : 0, 8, state.active ? 0.9 : 0]
      }
    });
    if (!runtime.getLayer('fuel-labels')) runtime.addLayer({
      id: 'fuel-labels', type: 'symbol', source: 'fuel-prices', minzoom: 9,
      layout: {
        'text-field': ['number-format', ['get', 'p'], { 'min-fraction-digits': 2, 'max-fraction-digits': 2 }],
        'text-size': 11, 'text-offset': [1.2, 0], 'text-anchor': 'left', 'text-allow-overlap': false, 'text-font': state.textFont
      },
      paint: { 'text-color': '#333333', 'text-halo-color': 'rgba(255,255,255,0.9)', 'text-halo-width': 1.5, 'text-opacity': state.active ? 1 : 0 }
    });
    bind('fuel-click', 'click', 'fuel-circles', (event) => {
      const feature = event.features?.[0] as FuelFeature | undefined;
      if (!feature || !fuelState) return;
      events.showPopup?.({
        coordinate: [event.lngLat.lng, event.lngLat.lat], offset: 10,
        html: `<strong>${escapeHtml(brandLabel(feature))}</strong><br>${escapeHtml(fuelState.fuelTypeLabel)}: <strong>${escapeHtml(formatPrice(feature, fuelState.priceMode))}</strong><br><small>${escapeHtml(feature.properties.a)}</small>`
      });
    });
    bind('fuel-enter', 'mouseenter', 'fuel-circles', () => { runtime.getCanvas().style.cursor = 'pointer'; });
    bind('fuel-leave', 'mouseleave', 'fuel-circles', () => { runtime.getCanvas().style.cursor = ''; });
    const filter = mixedFuelUnits ? ['has', 'p'] : ['all', ['>=', ['get', 'p'], state.range.min], ['<=', ['get', 'p'], state.range.max]];
    ['fuel-circles', 'fuel-labels'].forEach((id) => {
      if (!runtime.getLayer(id)) return;
      runtime.setFilter(id, filter);
      runtime.setLayoutProperty(id, 'visibility', state.active ? 'visible' : 'none');
    });
    const colorRange = state.mode === 'absolute' ? [globalMin, globalMax] : (() => {
      const visible = runtime.queryRenderedFeatures({ layers: ['fuel-circles'] })
        .map((feature) => Number(feature.properties?.p || 0)).filter(Number.isFinite);
      return visible.length < 2 ? [globalMin, globalMax] : [Math.min(...visible), Math.max(...visible)];
    })();
    runtime.setPaintProperty('fuel-circles', 'circle-color', fuelColorExpr(colorRange[0], colorRange[1]));
    runtime.setPaintProperty('fuel-circles', 'circle-opacity', ['interpolate', ['linear'], ['zoom'], 2, state.active ? 0.7 : 0, 8, state.active ? 0.9 : 0]);
    runtime.setPaintProperty('fuel-labels', 'text-opacity', state.active ? 1 : 0);
  };

  const refreshFuelViewport = () => {
    if (!fuelState?.active || !runtime.getLayer('fuel-circles')) {
      events.onFuelHistogram?.(null);
      return;
    }
    const prices = runtime.queryRenderedFeatures({ layers: ['fuel-circles'] })
      .map((feature) => Number(feature.properties?.p || 0))
      .filter((value) => Number.isFinite(value) && value > 0 && value < 20);
    events.onFuelHistogram?.(prices.length < 3 ? null : { prices, mixedUnits: mixedFuelUnits });
    if (fuelState.mode === 'relative' && prices.length >= 2) {
      runtime.setPaintProperty('fuel-circles', 'circle-color', fuelColorExpr(Math.min(...prices), Math.max(...prices)));
    }
  };

  const applyTollsRuntime = () => {
    if (disposed || !runtime.isStyleLoaded() || !tollState) return;
    const state = tollState;
    if (!state.data) {
      if (runtime.getLayer('toll-booth-dots')) runtime.setPaintProperty('toll-booth-dots', 'circle-opacity', 0);
      if (runtime.getLayer('toll-booth-labels')) runtime.setPaintProperty('toll-booth-labels', 'text-opacity', 0);
      return;
    }
    const source = runtime.getSource('toll-booths');
    if (source) source.setData(state.data);
    else runtime.addSource('toll-booths', { type: 'geojson', data: state.data });
    if (!runtime.getLayer('toll-booth-dots')) runtime.addLayer({
      id: 'toll-booth-dots', type: 'circle', source: 'toll-booths', filter: activeRouteFilter(state.activeRouteNums),
      paint: { 'circle-radius': ['interpolate', ['linear'], ['zoom'], 7, 3, 12, 6], 'circle-color': '#f97316', 'circle-stroke-color': '#ffffff', 'circle-stroke-width': 1.5, 'circle-opacity': state.active ? 0.9 : 0 }
    });
    if (!runtime.getLayer('toll-booth-labels')) runtime.addLayer({
      id: 'toll-booth-labels', type: 'symbol', source: 'toll-booths', minzoom: 10, filter: activeRouteFilter(state.activeRouteNums),
      layout: { 'text-field': ['coalesce', ['get', 'toll_role_label'], ['get', 'payment_label'], 'Toll'], 'text-size': 11, 'text-font': state.textFont, 'text-offset': [0, 1.1], 'text-allow-overlap': false },
      paint: { 'text-color': '#7c2d12', 'text-halo-color': '#fff7ed', 'text-halo-width': 1.5, 'text-opacity': state.active ? 1 : 0 }
    });
    bind('toll-click', 'click', 'toll-booth-dots', (event) => {
      const feature = event.features?.[0];
      if (!feature) return;
      const props = feature.properties || {};
      events.showPopup?.({ coordinate: feature.geometry.coordinates, offset: 12, html: `<strong>${escapeHtml(props.name || props.payment_label || 'Toll booth')}</strong><br>${escapeHtml(props.toll_role_label || props.toll_role || 'unknown')}<br><small>${escapeHtml(props.toll_role_confidence)}</small>` });
    });
    ['toll-booth-dots', 'toll-booth-labels'].forEach((id) => runtime.setFilter(id, activeRouteFilter(state.activeRouteNums)));
    runtime.setPaintProperty('toll-booth-dots', 'circle-opacity', state.active ? 0.9 : 0);
    runtime.setPaintProperty('toll-booth-labels', 'text-opacity', state.active ? 1 : 0);
  };

  const applyTrafficRuntime = () => {
    if (disposed || !runtime.isStyleLoaded() || !trafficState) return;
    removeLayer(runtime, 'tomtom-traffic-flow'); removeSource(runtime, 'tomtom-traffic-flow-source');
    if (!trafficState.active) return;
    runtime.addSource('tomtom-traffic-flow-source', { type: 'raster', tiles: [`/traffic/tomtom/flow/${trafficState.style}/{z}/{x}/{y}.png`], tileSize: 256, attribution: '<a href="https://www.tomtom.com/">TomTom</a>' });
    runtime.addLayer({ id: 'tomtom-traffic-flow', type: 'raster', source: 'tomtom-traffic-flow-source', paint: { 'raster-opacity': 0.72, 'raster-fade-duration': 0 } }, firstSymbolLayerId(runtime));
  };

  const applyBaustellenRuntime = () => {
    if (disposed || !runtime.isStyleLoaded() || !baustellenState) return;
    const state = baustellenState;
    if (!state.active || !state.data) {
      ['munich-baustellen-labels', 'munich-baustellen-outline', 'munich-baustellen-fill'].forEach((id) => removeLayer(runtime, id));
      removeSource(runtime, 'munich-baustellen-source');
      return;
    }
    const source = runtime.getSource('munich-baustellen-source');
    if (source) source.setData(state.data); else runtime.addSource('munich-baustellen-source', { type: 'geojson', data: state.data });
    if (!runtime.getLayer('munich-baustellen-fill')) runtime.addLayer({ id: 'munich-baustellen-fill', type: 'fill', source: 'munich-baustellen-source', paint: { 'fill-color': '#f97316', 'fill-opacity': 0.22 } }, firstSymbolLayerId(runtime));
    if (!runtime.getLayer('munich-baustellen-outline')) runtime.addLayer({ id: 'munich-baustellen-outline', type: 'line', source: 'munich-baustellen-source', paint: { 'line-color': '#fb923c', 'line-width': ['interpolate', ['linear'], ['zoom'], 9, 1, 14, 3], 'line-opacity': 0.9 } });
    if (!runtime.getLayer('munich-baustellen-labels')) runtime.addLayer({
      id: 'munich-baustellen-labels', type: 'symbol', source: 'munich-baustellen-source', minzoom: 12,
      layout: { 'text-field': ['coalesce', ['get', 'strassenname'], ['get', 'strasse'], ['get', 'name'], ['get', 'titel'], 'Baustelle'], 'text-size': 11, 'text-font': state.textFont, 'text-offset': [0, 0.8] },
      paint: { 'text-color': '#7c2d12', 'text-halo-color': '#fff7ed', 'text-halo-width': 1.4 }
    });
    const popupHandler = (event: any) => {
      const feature = event.features?.[0];
      if (!feature || !event.lngLat) return;
      const props = feature.properties || {};
      const infoUrl = extractFirstHttpUrl(props.weitere_info);
      const title = props.strasse_hausnr || 'Baustelle München';
      const html = '<div style="font:13px/1.5 system-ui,sans-serif;max-width:260px">'
        + `<strong>${escapeHtml(title)}</strong><br>`
        + (props.beeintraechtigung ? `<span style="color:#7f1d1d;font-weight:700">${escapeHtml(props.beeintraechtigung)}</span><br>` : '')
        + (props.betroffene_bereiche ? `<span style="color:#444">${escapeHtml(props.betroffene_bereiche)}</span><br>` : '')
        + (props.beginn_datum_kombiniert || props.ende_datum_kombiniert ? `<span style="color:#666">${escapeHtml(props.beginn_datum_kombiniert || '?')} - ${escapeHtml(props.ende_datum_kombiniert || '?')}</span><br>` : '')
        + (props.beschreibung ? `<span style="color:#666">${escapeHtml(props.beschreibung)}</span><br>` : '')
        + (infoUrl ? `<a href="${escapeHtml(infoUrl)}" target="_blank" rel="noopener noreferrer">More info</a>` : '')
        + '</div>';
      events.showPopup?.({ coordinate: [event.lngLat.lng, event.lngLat.lat], offset: 10, html });
    };
    ['munich-baustellen-fill', 'munich-baustellen-outline'].forEach((layer) => {
      bind(`baustellen-click:${layer}`, 'click', layer, popupHandler);
      bind(`baustellen-enter:${layer}`, 'mouseenter', layer, () => { runtime.getCanvas().style.cursor = 'pointer'; });
      bind(`baustellen-leave:${layer}`, 'mouseleave', layer, () => { runtime.getCanvas().style.cursor = ''; });
    });
  };

  const difficultyLayerIds = ['routes-hairpins-labels', 'routes-hairpins-dot', 'routes-difficulty-line', 'routes-difficulty-outline'];
  const hideDifficulty = () => difficultyLayerIds.forEach((id) => { if (runtime.getLayer(id)) runtime.setLayoutProperty(id, 'visibility', 'none'); });
  const applyDifficultyRuntime = () => {
    if (disposed || !runtime.isStyleLoaded() || !difficultyState) return;
    const state = difficultyState;
    if (!state.active || !state.segments || !state.hairpins) { hideDifficulty(); return; }
    const segmentSource = runtime.getSource('routes-difficulty-source');
    if (segmentSource) segmentSource.setData(state.segments); else runtime.addSource('routes-difficulty-source', { type: 'geojson', data: state.segments });
    const hairpinSource = runtime.getSource('routes-hairpins-source');
    if (hairpinSource) hairpinSource.setData(state.hairpins); else runtime.addSource('routes-hairpins-source', { type: 'geojson', data: state.hairpins });
    if (!runtime.getLayer('routes-difficulty-outline')) runtime.addLayer({ id: 'routes-difficulty-outline', type: 'line', source: 'routes-difficulty-source', layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#ffffff', 'line-width': ['interpolate', ['linear'], ['zoom'], 3, 5, 10, 10], 'line-opacity': 0.72 } });
    if (!runtime.getLayer('routes-difficulty-line')) runtime.addLayer({ id: 'routes-difficulty-line', type: 'line', source: 'routes-difficulty-source', layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': ['get', state.mode === 'curves' ? 'curve_color' : 'slope_color'], 'line-width': ['interpolate', ['linear'], ['zoom'], 3, 3, 10, 7], 'line-opacity': 0.9 } });
    if (!runtime.getLayer('routes-hairpins-dot')) runtime.addLayer({ id: 'routes-hairpins-dot', type: 'circle', source: 'routes-hairpins-source', paint: { 'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 5, 11, 9], 'circle-color': ['match', ['get', 'confidence'], 'high', '#be123c', 'medium', '#f97316', '#facc15'], 'circle-stroke-width': 2, 'circle-stroke-color': '#ffffff' } });
    if (!runtime.getLayer('routes-hairpins-labels')) runtime.addLayer({ id: 'routes-hairpins-labels', type: 'symbol', source: 'routes-hairpins-source', layout: { 'text-field': ['get', 'label'], 'text-size': 11, 'text-font': state.textFont, 'text-allow-overlap': true }, paint: { 'text-color': '#ffffff', 'text-halo-color': '#111827', 'text-halo-width': 1.4 } });
    difficultyLayerIds.forEach((id) => runtime.setFilter(id, activeRouteFilter(state.activeRouteNums)));
    runtime.setPaintProperty('routes-difficulty-line', 'line-color', ['get', state.mode === 'curves' ? 'curve_color' : 'slope_color']);
    const showLines = state.mode !== 'hairpins';
    ['routes-difficulty-outline', 'routes-difficulty-line'].forEach((id) => runtime.setLayoutProperty(id, 'visibility', showLines ? 'visible' : 'none'));
    ['routes-hairpins-dot', 'routes-hairpins-labels'].forEach((id) => runtime.setLayoutProperty(id, 'visibility', showLines ? 'none' : 'visible'));
  };

  const replay = () => {
    applyFuelRuntime(); applyTollsRuntime(); applyTrafficRuntime(); applyBaustellenRuntime(); applyDifficultyRuntime();
  };
  const unregister = registerLayerGroup('journey', replay);

  return {
    setEvents(nextEvents) { events = { ...events, ...nextEvents }; },
    applyFuel(state) { fuelState = state; applyFuelRuntime(); },
    refreshFuelViewport,
    applyTolls(state) { tollState = state; applyTollsRuntime(); },
    applyTraffic(state) { trafficState = state; applyTrafficRuntime(); },
    applyBaustellen(state) { baustellenState = state; applyBaustellenRuntime(); },
    applyDifficulty(state) { difficultyState = state; applyDifficultyRuntime(); },
    dispose() {
      if (disposed) return;
      disposed = true;
      unregister();
      handlers.forEach(({ event, layer, handler }) => runtime.off?.(event, layer, handler));
      handlers.clear();
      fuelState = tollState = trafficState = baustellenState = difficultyState = null;
    }
  };
}
